import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PartyPopper, Frown, Bell, CheckCheck } from "lucide-react";
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "@/lib/afritask";

const ICON_BY_TYPE = {
  selection_won: { icon: PartyPopper, tone: "text-emerald-600 bg-emerald-50" },
  selection_missed: { icon: Frown, tone: "text-neutral-500 bg-neutral-100" },
};

function fmtDayHeader(iso) {
  const d = new Date(iso);
  const now = new Date();
  const ymd = (x) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (ymd(d) === ymd(now)) return "Today";
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  if (ymd(d) === ymd(y)) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Notifications() {
  const { me } = useOutletContext();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    try {
      const d = await listNotifications(200);
      setItems(d.notifications);
      setUnread(d.unread);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (me) load();
  }, [me]);

  const grouped = items.reduce((acc, n) => {
    const key = fmtDayHeader(n.created_at);
    acc[key] = acc[key] || [];
    acc[key].push(n);
    return acc;
  }, {});

  const handleClick = async (n) => {
    if (!n.read) {
      await markNotificationRead(n.notification_id).catch(() => {});
      load();
    }
    if (n.type === "selection_won" && n.task_id) {
      navigate(`/app/tasks/${n.task_id}/workspace`);
    } else if (n.task_id) {
      navigate(`/app/tasks/${n.task_id}`);
    }
  };

  const handleMarkAll = async () => {
    await markAllNotificationsRead().catch(() => {});
    load();
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-neutral-500">
            Notifications
          </p>
          <p className="mt-1 text-sm text-neutral-600">
            Selection results, payouts and round updates all land here.
          </p>
        </div>
        {unread > 0 && (
          <Button
            onClick={handleMarkAll}
            variant="outline"
            size="sm"
            data-testid="notif-mark-all"
          >
            <CheckCheck className="mr-1.5 h-3.5 w-3.5" /> Mark all read
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <div className="rounded-full bg-neutral-100 p-4 text-neutral-400">
              <Bell className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold">No notifications yet</h3>
            <p className="max-w-md text-sm text-neutral-500">
              Enter a round — you&apos;ll be notified as soon as selection
              completes.
            </p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).map(([day, ns]) => (
          <section key={day} data-testid={`notif-group-${day}`}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-neutral-500">
              {day}
            </h2>
            <Card>
              <ul className="divide-y divide-neutral-100">
                {ns.map((n) => {
                  const meta = ICON_BY_TYPE[n.type] || {
                    icon: Bell,
                    tone: "text-neutral-500 bg-neutral-100",
                  };
                  const Icon = meta.icon;
                  return (
                    <li key={n.notification_id}>
                      <button
                        onClick={() => handleClick(n)}
                        className={`flex w-full items-start gap-4 px-6 py-4 text-left transition hover:bg-neutral-50 ${
                          n.read ? "" : "bg-amber-50/40"
                        }`}
                        data-testid={`notif-row-${n.notification_id}`}
                      >
                        <span
                          className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${meta.tone}`}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <p className="text-sm font-semibold text-neutral-900">
                              {n.title}
                            </p>
                            <span className="shrink-0 text-[10px] text-neutral-400">
                              {fmtTime(n.created_at)}
                            </span>
                          </div>
                          <p className="mt-1 text-sm leading-snug text-neutral-600">
                            {n.body}
                          </p>
                          {!n.read && (
                            <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-amber-600" />
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </section>
        ))
      )}
    </div>
  );
}
